import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {Timestamp} from 'firebase/firestore';

dayjs.extend(utc);

type DateLike = Date | string;

const toValidDayjs = (input: DateLike, fnName: string): dayjs.Dayjs => {
  const d = dayjs.utc(input);
  if (!d.isValid()) {
    throw new Error(`${fnName}: invalid date input "${input}"`);
  }
  return d;
};

/**
 * Given a start and end date string in the format yyyy-mm-dd, return an array of dates in the range.
 *
 * @example
 * getDatesInRange('2025-01-01', '2025-01-03') // ['2025-01-01', '2025-01-02', '2025-01-03']
 */
const getDatesInRange = (startDate: string, endDate: string): string[] => {
  let current = toValidDayjs(startDate, 'getDatesInRange');
  const end = toValidDayjs(endDate, 'getDatesInRange');
  const dates: string[] = [];

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }

  return dates;
};

/**
 * Converts a Firebase Timestamp or Date object to a formatted string in the format yyyy-mm-dd hh:mm or yyyy-mm-dd.
 *
 * @param date - The date to be converted, either a Firebase Timestamp or a JavaScript Date.
 * @param includeTime - Optional parameter to include time in the formatted string. Default is true.
 * @returns A string representing the formatted date and time.
 */
const formatDateToString = (date: Timestamp | Date, includeTime = true): string => {
  const d = date instanceof Timestamp
    ? dayjs(date.seconds * 1000)
    : dayjs(date);

  if (!includeTime) {
    return d.format('YYYY-MM-DD');
  }
  return d.format('YYYY-MM-DD h:mm A');
};

/**
 * Formats a date as "Month D, YYYY, h:mm A" (e.g. "April 10, 2026, 2:30 PM"), for inline notes that read as prose.
 */
const formatMonthDayYearTime = (date: Timestamp | Date): string => {
  const d = date instanceof Timestamp
    ? dayjs(date.seconds * 1000)
    : dayjs(date);
  return d.format('MMMM D, YYYY, h:mm A');
};

/**
 * Parses a date input using UTC to avoid timezone-induced day shifts.
 */
const toUTCDate = (dateInput: DateLike): Date => {
  return toValidDayjs(dateInput, 'toUTCDate').startOf('day').toDate();
};

/**
 * Shifts a date stored at UTC midnight onto the same wall-clock day locally, so formatting it shows
 * the day it was stored on rather than the day before in a negative offset.
 */
const toLocalDay = (dateInput: DateLike | number): Date => {
  const date = new Date(dateInput);
  date.setHours(date.getHours() + (date.getTimezoneOffset() / 60));
  return date;
};

/**
 * Returns the last day of the same month as dateInput (UTC-based).
 */
const getLastDayOfMonth = (dateInput: DateLike): Date => {
  return toValidDayjs(dateInput, 'getLastDayOfMonth')
    .endOf('month')
    .startOf('day')
    .toDate();
};

/**
 * Returns the last day of the previous month relative to dateInput (UTC-based).
 */
const getLastDayOfPreviousMonth = (dateInput: DateLike): Date => {
  return toValidDayjs(dateInput, 'getLastDayOfPreviousMonth')
    .subtract(1, 'month')
    .endOf('month')
    .startOf('day')
    .toDate();
};

/**
 * Returns true if the given date is the last day of its month.
 */
const isLastDayOfMonth = (input: DateLike): boolean => {
  const d = dayjs.utc(input);
  if (!d.isValid()) return false;
  return d.date() === d.endOf('month').date();
};

/** Millis of an audit stamp's `timestamp` in any form it arrives in; null when missing or unparseable. */
const stampMillis = (stamp: unknown): number | null => {
  const ts = (stamp as {timestamp?: unknown} | null | undefined)?.timestamp;
  if (ts == null) return null;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'object' && typeof (ts as {toMillis?: unknown}).toMillis === 'function') {
    return (ts as {toMillis: () => number}).toMillis();
  }
  const n = typeof ts === 'string' ? Date.parse(ts) : Number(ts);
  return Number.isNaN(n) ? null : n;
};

export {
  getDatesInRange,
  formatDateToString,
  stampMillis,
  formatMonthDayYearTime,
  toUTCDate,
  toLocalDay,
  getLastDayOfMonth,
  getLastDayOfPreviousMonth,
  isLastDayOfMonth,
};

export type {DateLike};
